import { useEffect, useMemo, useState } from 'react';
import { debounce, compact } from 'lodash-es';
import { Autocomplete, Typography } from 'seagull';
import { useGoogleMaps } from "./GoogleMapsProvider";
import { PlacesServiceStatus } from "./loadGoogleMaps";
import { useAppConfig } from "../../store/store-context";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getFormattedPlace(place) {
  if (!place.address_components) {
    return null;
  }
  const findByType = type => {
    var _place$address_compon;
    return (_place$address_compon = place.address_components) === null || _place$address_compon === void 0 ? void 0 : _place$address_compon.find(_ref => {
      let {
        types
      } = _ref;
      return types.includes(type);
    });
  };
  const streetNumber = findByType('street_number');
  const route = findByType('route');
  const city = findByType('locality') || findByType('sublocality');
  const state = findByType('administrative_area_level_1');
  const country = findByType('country');
  const postalCode = findByType('postal_code');
  return {
    address1: compact([streetNumber === null || streetNumber === void 0 ? void 0 : streetNumber.long_name, route === null || route === void 0 ? void 0 : route.long_name]).join(', '),
    address2: '',
    city: (city === null || city === void 0 ? void 0 : city.long_name) || '',
    state: (state === null || state === void 0 ? void 0 : state.short_name) || '',
    country: (country === null || country === void 0 ? void 0 : country.short_name) || '',
    postalCode: (postalCode === null || postalCode === void 0 ? void 0 : postalCode.long_name) || ''
  };
}
export default function AddressAutocomplete(props) {
  const {
    textFieldProps,
    testid,
    inputValue,
    onInputChange,
    onSelectPlace,
    onBlur: _onBlur
  } = props;
  const googleMaps = useGoogleMaps();
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(inputValue || '');
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [open, setOpen] = useState(false);
  const {
    inputDebounceTime
  } = useAppConfig();

  // This allows the component to be controlled.
  useEffect(() => {
    if (inputValue != null) {
      setValue(inputValue);
    }
  }, [inputValue]);
  useEffect(() => {
    const newIsOpen = !!options.length && isFocused;
    setOpen(newIsOpen);
  }, [options, isFocused]);
  const debouncedGetPlacePredictions = useMemo(() => debounce((value, googleMaps) => {
    if (!value || !googleMaps) return setOptions([]);
    googleMaps.getPlacePredictions({
      input: value,
      componentRestrictions: {
        country: 'us'
      }
    }, (predictions, status) => {
      if (status !== PlacesServiceStatus.NOT_FOUND && status !== PlacesServiceStatus.ZERO_RESULTS && status !== PlacesServiceStatus.OK) {
        return;
      }
      setOptions(predictions || []);
    });
  }, inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500), []);
  useEffect(() => {
    if (isFocused) {
      debouncedGetPlacePredictions(value, googleMaps);
    }
  }, [value, googleMaps, isFocused, debouncedGetPlacePredictions]);
  return /*#__PURE__*/_jsx(Autocomplete, {
    blurOnSelect: true,
    disableEndAdornment: true,
    selectOnFocus: false,
    clearOnBlur: false,
    "data-testid": testid,
    textFieldProps: textFieldProps,
    options: options,
    value: selectedOption,
    inputValue: value,
    open: open,
    onFocus: () => setIsFocused(true),
    onBlur: e => {
      setIsFocused(false);
      _onBlur && _onBlur(e);
    },
    filterOptions: opt => opt,
    getOptionLabel: option => {
      if (!option) return '';
      return typeof option === 'string' ? option : option.description;
    },
    renderOption: option => /*#__PURE__*/_jsxs(Typography, {
      variant: "paragraph3",
      children: [/*#__PURE__*/_jsx(Typography, {
        as: "span",
        variant: "paragraph2",
        weight: "bold",
        children: option.structured_formatting.main_text
      }), ' ', option.structured_formatting.secondary_text]
    }),
    getOptionSelected: (option, value) => option.place_id === value.place_id,
    onChange: (_, value) => {
      const newSelectedOption = value ? value : null;
      setSelectedOption(newSelectedOption);
      if (!newSelectedOption || !googleMaps) {
        return onSelectPlace && onSelectPlace(null);
      }
      googleMaps.getPlaceDetails({
        placeId: newSelectedOption.place_id
      }, (result, status) => {
        if (status !== PlacesServiceStatus.NOT_FOUND && status !== PlacesServiceStatus.ZERO_RESULTS && status !== PlacesServiceStatus.OK || !result) {
          return;
        }
        if (onSelectPlace) {
          onSelectPlace({
            raw: result,
            formatted: getFormattedPlace(result)
          });
        }
      });
    },
    onInputChange: function (_onInputChange) {
      function onInputChange(_x, _x2) {
        return _onInputChange.apply(this, arguments);
      }
      onInputChange.toString = function () {
        return _onInputChange.toString();
      };
      return onInputChange;
    }((event, newValue) => {
      if (!event) return;
      setValue(newValue);
      const eventToPropagate = {
        target: {
          value: newValue
        }
      };
      onInputChange && onInputChange(eventToPropagate);
    })
  });
}